import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  FolderOpen, User, Image, Film, FileText, Music, Download,
  Loader2, Search, ChevronRight, ExternalLink, Camera, Star,
  BookOpen
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './FileBrowser.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FileBrowser = () => {
  const [artists, setArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistFiles, setArtistFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getToken = () => localStorage.getItem('adminToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  useEffect(() => { fetchArtists(); }, []);

  const fetchArtists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/artists`, { headers: getHeaders() });
      if (res.ok) setArtists(await res.json());
    } catch { toast.error('Failed to load artists'); }
    finally { setLoading(false); }
  };

  const selectArtist = async (artist) => {
    setSelectedArtist(artist);
    setFilesLoading(true);
    setArtistFiles(null);
    try {
      const [detailRes, mediaRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/artists/${artist.id}`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/staff/artists/${artist.id}/media`, { headers: getHeaders() })
      ]);
      const detail = detailRes.ok ? await detailRes.json() : {};
      const media = mediaRes.ok ? await mediaRes.json() : {};
      const subs = detail.submissions || [];

      // Also get releases for this artist
      const profile = detail.profile || {};
      const account = detail.account || {};
      const slug = account.artist_name ? account.artist_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') : null;

      let releases = [];
      if (slug) {
        try {
          const relRes = await fetch(`${API_URL}/api/artists/${slug}/releases`);
          if (relRes.ok) releases = await relRes.json();
        } catch {}
      }

      setArtistFiles({ profile, account, media, submissions: subs, releases, slug });
    } catch { toast.error('Failed to load files'); }
    finally { setFilesLoading(false); }
  };

  const proxyUrl = (url) => {
    if (!url) return '';
    return getImageUrl(url) || url;
  };

  const filteredArtists = artists.filter(a =>
    a.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const FileCard = ({ icon: Icon, name, type, url, thumb }) => (
    <a href={url} target="_blank" rel="noopener noreferrer" className="fb-file" data-testid={`file-${name}`}>
      {thumb ? (
        <img src={thumb} alt={name} className="fb-file-thumb" onError={e => { e.target.style.display = 'none'; }} />
      ) : (
        <div className="fb-file-icon"><Icon size={24} /></div>
      )}
      <div className="fb-file-info">
        <span className="fb-file-name" title={name}>{name}</span>
        <span className="fb-file-type">{type}</span>
      </div>
      <Download size={14} className="fb-file-dl" />
    </a>
  );

  const FolderSection = ({ icon: Icon, title, count, children }) => {
    if (count === 0) return null;
    return (
      <div className="fb-folder">
        <div className="fb-folder-header">
          <Icon size={16} />
          <h3>{title}</h3>
          <span className="fb-folder-count">{count}</span>
        </div>
        <div className="fb-folder-content">{children}</div>
      </div>
    );
  };

  if (loading) return <div className="file-browser"><div className="fb-loading"><Loader2 size={24} className="spinner" /> Loading...</div></div>;

  return (
    <div className="file-browser" data-testid="file-browser">
      <div className="fb-header">
        <h1>File Browser</h1>
        <p>Browse and download artist assets — read only</p>
      </div>

      <div className="fb-layout">
        {/* Artist list */}
        <div className="fb-sidebar">
          <div className="fb-search">
            <Search size={14} />
            <input type="text" placeholder="Search artists..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div className="fb-artist-list">
            {filteredArtists.map(a => (
              <div
                key={a.id}
                className={`fb-artist-item${selectedArtist?.id === a.id ? ' selected' : ''}`}
                onClick={() => selectArtist(a)}
                data-testid={`artist-${a.id}`}
              >
                <User size={16} />
                <div>
                  <span className="fb-artist-name">{a.artist_name}</span>
                  <span className="fb-artist-status">{a.status}</span>
                </div>
                <ChevronRight size={14} className="fb-chevron" />
              </div>
            ))}
          </div>
        </div>

        {/* Files panel */}
        <div className="fb-content">
          {!selectedArtist ? (
            <div className="fb-empty"><FolderOpen size={48} /><p>Select an artist to browse files</p></div>
          ) : filesLoading ? (
            <div className="fb-empty"><Loader2 size={24} className="spinner" /><p>Loading files...</p></div>
          ) : artistFiles ? (
            <>
              <div className="fb-content-header">
                <h2>{artistFiles.account.artist_name}</h2>
                <span className="fb-artist-email">{artistFiles.account.email}</span>
              </div>

              {/* Profile PDF */}
              <FolderSection icon={BookOpen} title="Profile Data" count={1}>
                <FileCard
                  icon={FileText}
                  name={`${artistFiles.account.artist_name} - Profile.pdf`}
                  type="Auto-generated PDF"
                  url={`${API_URL}/api/admin/artists/${selectedArtist.id}/profile-pdf`}
                />
              </FolderSection>

              {/* Press Kit PDF */}
              {artistFiles.slug && (
                <FolderSection icon={FileText} title="Press Kit" count={1}>
                  <FileCard
                    icon={FileText}
                    name={`${artistFiles.account.artist_name} - Press Kit.pdf`}
                    type="Press Kit PDF"
                    url={`${API_URL}/api/presskit/${artistFiles.slug}/pdf`}
                  />
                </FolderSection>
              )}

              {/* Profile Images */}
              {(() => {
                const imgs = [
                  artistFiles.profile.profile_image && { name: 'Profile Image', url: artistFiles.profile.profile_image },
                  artistFiles.profile.banner_image && { name: 'Banner Image', url: artistFiles.profile.banner_image },
                ].filter(Boolean);
                return (
                  <FolderSection icon={User} title="Profile Images" count={imgs.length}>
                    {imgs.map((img, i) => (
                      <FileCard key={i} icon={Image} name={img.name} type="Image" url={proxyUrl(img.url)} thumb={proxyUrl(img.url)} />
                    ))}
                  </FolderSection>
                );
              })()}

              {/* Media Vault */}
              {Object.entries(artistFiles.media).map(([catKey, catData]) => {
                const files = catData.files || [];
                if (files.length === 0) return null;
                const icons = { editorial_portrait: Camera, lifestyle: Image, video_clip: Film, press_material: FileText, logo: Star };
                const CatIcon = icons[catKey] || FolderOpen;
                return (
                  <FolderSection key={catKey} icon={CatIcon} title={catData.label} count={files.length}>
                    {files.map(f => (
                      <FileCard
                        key={f.id}
                        icon={f.content_type?.startsWith('video/') ? Film : f.content_type?.startsWith('image/') ? Image : FileText}
                        name={f.original_name}
                        type={f.content_type?.split('/')[1]?.toUpperCase() || 'File'}
                        url={proxyUrl(f.file_url)}
                        thumb={f.content_type?.startsWith('image/') ? proxyUrl(f.file_url) : null}
                      />
                    ))}
                  </FolderSection>
                );
              })}

              {/* Track Submissions */}
              {artistFiles.submissions.length > 0 && (
                <FolderSection icon={Music} title="Track Submissions" count={artistFiles.submissions.length}>
                  {artistFiles.submissions.map((s, i) => (
                    <FileCard
                      key={s.id || i}
                      icon={Music}
                      name={s.title || `Submission ${i + 1}`}
                      type={`${s.submission_type || 'Track'} • ${s.track_genre || ''}`}
                      url={proxyUrl(s.file_url)}
                    />
                  ))}
                </FolderSection>
              )}

              {/* Release Artwork */}
              {artistFiles.releases.length > 0 && (
                <FolderSection icon={Image} title="Release Artwork" count={artistFiles.releases.length}>
                  {artistFiles.releases.map((r, i) => (
                    <FileCard
                      key={r.id || i}
                      icon={Image}
                      name={`${r.title} - Artwork`}
                      type="Release Cover"
                      url={proxyUrl(r.artwork)}
                      thumb={r.artwork ? proxyUrl(r.artwork) : null}
                    />
                  ))}
                </FolderSection>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FileBrowser;
